import { useLearningProgressExamList, useLearningProgressIndustries, useLearningProgressLearners } from "@/api/services/main/learning-progress";
import { TGetLearningProgressLearnersReqDto } from "@/api/services/main/learning-progress/dtos/get-learning-progress-learners.dto";
import { Button } from "@/components/elements/Button";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { InnerBox } from "@/components/elements/InnerBox";
import { Pagination } from "@/components/elements/Pagination";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { divider } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FC, useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LearningUserTable } from "@/features/learning-progress/components/LearningUserTable";
import { ExamSummarySection } from "@/features/learning-progress/components/exam/ExamSummarySection";
import {
  LEARNING_PROGRESS_NUMBER_RANGE_FIELDS,
  TLearningProgressFilterFormExtended,
  defaultLearningProgressFilterValues,
  isEmptyLearningProgressFilterValue,
  normalizeLearningProgressDateFields,
  normalizeLearningProgressLegacyFields,
  stripEmptyLearningProgressFilterFields,
} from "@/features/learning-progress/components/filter/learning-progress-filter-shared";
import { LearningProgressListTop } from "@/features/learning-progress/components/filter/LearningProgressListTop";
import { LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY } from "@/features/learning-progress/constants/filter";
import { parseIndustryScopedFilterMap } from "@/utils/industry-scoped-filter-storage";
import { IconLabProfile } from "@/assets/icons";
import { useInitSetup } from "@/providers/InitSetupProvider";
import { USER_TYPE } from "@/types/enum";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";

export const LearningProgressDetailPage: FC = () => {
  const { industryId } = useParams<{ industryId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const pagReq = usePagination();

  const numericIndustryId = industryId ? Number(industryId) : 0;
  const currentPage = pagReq.page || START_PAGE;
  const perPage = pagReq.perPage || DEFAULT_PER_PAGE;

  const { userType } = useInitSetup();
  const isRsoUser = userType === USER_TYPE.RSO_USER;

  const { data: industriesData } = useLearningProgressIndustries();
  const industry = industriesData?.data?.find((item) => item.id === numericIndustryId);

  const [advancedFilters, setAdvancedFilters] = useState<TLearningProgressFilterFormExtended | null>(() => {
    const map = parseIndustryScopedFilterMap(localStorage.getItem(LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY));
    const perIndustry = map[String(numericIndustryId)];
    if (!perIndustry) return null;
    return {
      ...defaultLearningProgressFilterValues,
      ...(normalizeLearningProgressLegacyFields(
        normalizeLearningProgressDateFields(perIndustry as Partial<TLearningProgressFilterFormExtended>) as Partial<Record<string, unknown>>,
      ) as Partial<TLearningProgressFilterFormExtended>),
    } as TLearningProgressFilterFormExtended;
  });

  const resetToFirstPage = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TLearningProgressFilterFormExtended) => {
      const hasAny = Object.values(filters).some((v) => !isEmptyLearningProgressFilterValue(v));
      const map = parseIndustryScopedFilterMap(localStorage.getItem(LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY));
      const industryKey = String(numericIndustryId);
      if (hasAny) {
        setAdvancedFilters(filters);
        map[industryKey] = stripEmptyLearningProgressFilterFields(filters);
      } else {
        setAdvancedFilters(null);
        delete map[industryKey];
      }
      if (Object.keys(map).length > 0) {
        localStorage.setItem(LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY, JSON.stringify(map));
      } else {
        localStorage.removeItem(LEARNING_PROGRESS_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage, numericIndustryId],
  );

  // Build the numeric-range portion from the shared config so adding/removing a range field
  // only requires editing LEARNING_PROGRESS_NUMBER_RANGE_FIELDS.
  const buildNumberRangeQuery = (filters: TLearningProgressFilterFormExtended): Record<string, number> => {
    const out: Record<string, number> = {};
    for (const { fromKey, toKey } of Object.values(LEARNING_PROGRESS_NUMBER_RANGE_FIELDS)) {
      if (filters[fromKey]) out[fromKey] = Number(filters[fromKey]);
      if (filters[toKey]) out[toKey] = Number(filters[toKey]);
    }
    return out;
  };

  const learnersQuery: TGetLearningProgressLearnersReqDto = {
    industryId: numericIndustryId,
    page: currentPage,
    perPage,
    sorts: pagReq.sorts,
    ...(advancedFilters && {
      ...(advancedFilters.fullName ? { fullName: advancedFilters.fullName } : {}),
      ...(advancedFilters.rsoCompanyIds.length > 0 ? { rsoCompanyIds: advancedFilters.rsoCompanyIds } : {}),
      ...(advancedFilters.nationalityIds.length > 0 ? { nationalityIds: advancedFilters.nationalityIds } : {}),
      ...(advancedFilters.japaneseLanguageLevels.length > 0 ? { japaneseLanguageLevels: advancedFilters.japaneseLanguageLevels } : {}),
      ...(advancedFilters.examIds.length > 0 ? { examIds: advancedFilters.examIds } : {}),
      ...(advancedFilters.curriculumProgresses.length > 0 ? { curriculumProgresses: advancedFilters.curriculumProgresses } : {}),
      ...(advancedFilters.lastLoginDateFrom ? { lastLoginDateFrom: dayjs(advancedFilters.lastLoginDateFrom).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.lastLoginDateTo ? { lastLoginDateTo: dayjs(advancedFilters.lastLoginDateTo).format("YYYY-MM-DD") } : {}),
      ...buildNumberRangeQuery(advancedFilters),
    }),
  };

  const { data: learnersData, isLoading } = useLearningProgressLearners(learnersQuery);

  const { data: examsData, isLoading: isLoadingExams } = useLearningProgressExamList({
    industryId: numericIndustryId,
  });
  const exams = examsData?.data ?? [];

  const learners = learnersData?.data ?? [];
  const pagination = learnersData?.pagination ?? {
    page: currentPage,
    perPage,
    total: 0,
    totalPage: 0,
    nextPage: null,
    prevPage: null,
    count: 0,
  };

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const handleGoToReportCreation = useCallback(() => {
    navigate(getDynamicRoute(AppRoutes.learningProgressReport, { industryId: String(numericIndustryId) }));
  }, [navigate, numericIndustryId]);

  return (
    <Stack gap={3}>
      <Typography variant="head20Bold">学習進捗の確認{industry?.name && ` - ${industry.name}`}</Typography>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high} loading={isLoadingExams}>
        <ExamSummarySection exams={exams} industryId={numericIndustryId} />
      </InnerBox>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack spacing={2}>
          <LearningProgressListTop
            pagination={pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            currentAdvancedFilters={advancedFilters}
            industryId={numericIndustryId}
          />
          <Stack spacing={3}>
            <LearningUserTable rows={learners} isLoading={isLoading} />
            <Pagination totalPage={pagination.totalPage} />
          </Stack>
        </Stack>
      </InnerBox>

      <FooterContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button size="sm" variant="ghost" onClick={handleGoBack}>
            戻る
          </Button>
          {isRsoUser && (
            <Button
              size="sm"
              variant="outline"
              startIcon={<IconLabProfile />}
              onClick={handleGoToReportCreation}
            >
              学習進捗報告書の作成
            </Button>
          )}
        </Stack>
      </FooterContainer>
    </Stack>
  );
};
